import { Options } from 'highcharts';
import { round } from 'lodash';
import React, { FC } from 'react';
import { TemplateInsightGroupBy } from 'api/template-insight';
import { Filter } from 'components/molecules/filters/types';
import { DateRange } from 'components/organisms/projects-report/common/range-picker/types';
import { useTranslate } from 'hooks/use-translation';
import { useTemplateInsightQuery } from 'queries/template-insight';
import { AutoHeightTable } from '../auto-heigh-table';
import { ChartTextStyle, Wrapper } from '../components';
import { DefaultEmptyChart, DefaultEmptyTable } from '../empty-state';
import { TemplateInsightBaseChart } from '../template-insight-base-chart';
import {
  TemplateOverviewTooltip,
  TooltipStyle,
} from '../template-insight-tooltip';
import { useTemplateInsightViewColumns } from '../templates/columns';

const getDefaultOptions = ({ yAxisTitle }): Options => ({
  chart: {
    type: 'column',
  },
  yAxis: {
    min: 0,
    title: {
      text: yAxisTitle,
    },
    ...ChartTextStyle,
  },
  tooltip: {
    shared: true,
    formatter() {
      const series =
        this.points?.map((point) => ({
          percent: round(point?.percentage ?? 0),
          val: point.y,
          name: point.series.name,
        })) ?? [];

      return TemplateOverviewTooltip({
        series,
        title: (this.x as unknown as string) ?? '',
      });
    },
    ...TooltipStyle,
  },
  plotOptions: {
    column: {
      stacking: 'percent',
    },
  },
  legend: {
    layout: 'horizontal',
    align: 'right',
    verticalAlign: 'top',
    itemStyle: ChartTextStyle.style,
  },
});

interface Props {
  range: DateRange;
  filter: Filter;
}

export const AllTemplate: FC<Props> = ({ range, filter }) => {
  const translate = useTranslate();
  const { data: templateInsight, isLoading } = useTemplateInsightQuery({
    range,
    groupBy: TemplateInsightGroupBy.Project,
    filter,
  });
  const meta =
    templateInsight?.templateResponses.map((templateMeta) => {
      const {
        projectsCompletedLate: percentageCompletedLate,
        template,
        projects,
        duration,
        completionTime,
        averageCSAT,
      } = templateMeta;
      const noOfProjects = projects.length;
      const projectsCompletedLate = round(
        (percentageCompletedLate / 100) * noOfProjects,
        2,
      );
      const completedOnTime = noOfProjects - projectsCompletedLate;

      return {
        template,
        templateName: template.templateName,
        completedOnTime,
        projectsCompletedLate,
        noOfProjects,
        plannedDuration: duration,
        average: {
          value: completionTime.averageDuration,
          cmpValue: duration - completionTime.averageDuration,
        },
        p50: {
          value: completionTime.p50,
          cmpValue: duration - completionTime.p50,
        },
        p90: {
          value: completionTime.p90,
          cmpValue: duration - completionTime.p90,
        },
        percentageCompletedLate: round(percentageCompletedLate),
        csat: averageCSAT ? round(averageCSAT, 1) : '',
      };
    }) ?? [];

  const options: Options = {
    ...getDefaultOptions({
      yAxisTitle: translate(
        'reports.template_insight.overview.project.chart.yAxisTitle',
      ),
    }),
    xAxis: {
      categories: meta.map((data) => data.templateName),
      ...ChartTextStyle,
    },
    series: [
      {
        name: translate(
          'reports.template_insight.overview.project.chart.on-time',
        ),
        type: 'column',
        data: meta.map(({ completedOnTime }) => completedOnTime),
        color: 'var(--green-50)',
        maxPointWidth: 64,
      },
      {
        name: translate(
          'reports.template_insight.overview.project.chart.delayed',
        ),
        type: 'column',
        data: meta.map(({ projectsCompletedLate }) => projectsCompletedLate),
        color: 'var(--red-60)',
        maxPointWidth: 64,
      },
    ],
  };

  const tableColumn = useTemplateInsightViewColumns();

  return (
    <Wrapper>
      <TemplateInsightBaseChart
        chartOption={options}
        title={translate(
          'reports.template_insight.overview.project.chart.title',
        )}
        tooltip={translate(
          'reports.template_insight.overview.project.chart.description',
        )}
        renderEmpty={() =>
          DefaultEmptyChart({
            noDataKey: 'report.chart.empty.overview.template',
          })
        }
        isLoading={isLoading}
      />
      <AutoHeightTable
        dataSource={meta}
        columns={tableColumn}
        titleLabel={translate(
          'reports.template_insight.overview.template.table.title',
        )}
        isLoading={isLoading}
        renderEmpty={() =>
          DefaultEmptyTable({
            noDataKey: 'report.table.empty.overview.template',
          })
        }
      />
    </Wrapper>
  );
};
